import { useContext, useEffect, useMemo, useState } from 'react'
import { useLang } from '_metronic/i18n/Metronici18n'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'

import DateFilterToolbar from 'Presentation/Components/CustomToolbar/DateFilter'
import DoubleLineImage from 'Presentation/Components/Table/Cells/DoubleLineImage'
import AmountPercentage from 'Presentation/Components/Table/Cells/AmountPercentage'
import CustomerChip from './Components/Cells/CustomerChip'

import { useDashboardStore } from 'Store/Dashboard'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { generateImageUrl } from 'Utils/generateImageUrl'
import moment from 'moment'
import 'moment-timezone'

const STEPPERS: {
	thLabel: string
	enLabel: string
	status: 'active' | 'done' | 'pending'
	description?: string | undefined
}[] = [
	{
		thLabel: 'คำร้องใหม่',
		enLabel: 'New Inquiries',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ส่งใบเสนอราคาแล้ว',
		enLabel: 'Quotation Sent',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ยืนยันใบเสนอราคา',
		enLabel: 'Quotation Confirmed',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ชำระเงิน',
		enLabel: 'Payment Completed',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ยืนยันตัวอย่าง',
		enLabel: 'Sample Approved',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'กำลังเดินการผลิต',
		enLabel: 'In Production',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'จัดส่งแล้ว',
		enLabel: 'Delivered',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'จัดส่งสำเร็จ',
		enLabel: 'Transaction Completed',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ยกเลิก',
		enLabel: 'Canceled',
		status: 'done',
		description: '',
	},
	{
		thLabel: 'ข้อพิพาท',
		enLabel: 'Disputed',
		status: 'done',
		description: '',
	},
]

const INITIAL_FILTER_STATE: {
	startPeriod: string | null
	endPeriod: string | null
} = {
	startPeriod: moment()
		.tz('Asia/Bangkok')
		.set({
			hour: 0,
			minute: 0,
			second: 0,
			millisecond: 0,
		})
		.format('YYYY-MM-DD HH:mm'),
	endPeriod: moment()
		.tz('Asia/Bangkok')
		.set({
			hour: 23,
			minute: 59,
			second: 59,
			millisecond: 59,
		})
		.format('YYYY-MM-DD HH:mm'),
}

const ViewModel = () => {
	const lang = useLang()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { reportData, getData, clearState } = useDashboardStore(
		state => ({
			reportData: state.data,
			getData: state.getData,
			clearState: state.clearState,
		}),
		shallow
	)
	const { updateLoading, updateError } = useContext(TableContext)
	const [filter, setFilter] = useState(INITIAL_FILTER_STATE)

	const steppers = useMemo(() => {
		const stepMapper: { [key: number]: string } = {
			0: 'newRequest',
			1: 'quoted',
			2: 'accepted',
			3: 'paymentCompleted',
			4: 'sampleApproved',
			5: 'inProduction',
			6: 'delivered',
			7: 'completed',
			8: 'cancelled',
			9: 'dispute',
		}

		return STEPPERS.map((s, idx) => ({
			label: lang === 'en' ? s.enLabel : s.thLabel,
			value: reportData?.['summaryTransactionStatus']?.[stepMapper[idx]] || '0',
			status: 'done' as 'done' | 'active' | 'pending',
			description: '',
		}))
	}, [lang, reportData])

	const buyerData = useMemo(() => {
		const rawData = reportData?.topSaleBuyers ?? []

		return rawData
	}, [reportData])

	const fetchData = () => {
		if (filter.startPeriod === null || filter.endPeriod === null) return

		updateLoading(true)
		updateError(false)
		getData(filter).then(({ success, data }) => {
			if (!success) {
				toast.error(data)
				updateError(true)
				updateLoading(false)
			} else {
				updateLoading(false)
				updateError(false)
			}
		})
	}

	const onChangeDateFilter = (
		type: 'today' | 'thisWeek' | 'thisMonth' | 'custom',
		key?: string,
		value?: any
	) => {
		if (type === 'today') {
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisWeek') {
			// This week is start from Thrusday to Wednesday
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.startOf('week')
					.add(3, 'days')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.endOf('week')
					.add(3, 'days')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisMonth') {
			setFilter({
				startPeriod: moment().tz('Asia/Bangkok').startOf('month').format('YYYY-MM-DD HH:mm'),
				endPeriod: moment().tz('Asia/Bangkok').endOf('month').format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'custom') {
			if (key === 'startPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 0,
									minute: 0,
									second: 0,
									millisecond: 0,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					startPeriod: finale,
				})
			} else if (key === 'endPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 23,
									minute: 59,
									second: 59,
									millisecond: 59,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					endPeriod: finale,
				})
			}
		}
	}

	const TAB_CONFIGS: any[] = [
		{
			Header: 'ชื่อและรหัสลูกค้า',
			accessor: 'customer',
			width: 600,
			minWidth: 600,
			Cell: ({ row }: any) => (
				<DoubleLineImage
					img={
						row.original?.buyerPictureProfilePath
							? generateImageUrl(row.original?.buyerPictureProfilePath)
							: null
					}
					label={row.original?.buyerTitle}
					description={row.original?.buyerEmail}
				/>
			),
		},
		{
			Header: 'ประเภทลูกค้า',
			accessor: 'customer_type',
			width: 100,
			minWidth: 100,
			Cell: ({ row }: any) => <CustomerChip value={'Standard'} />,
		},
		{
			Header: 'ยอดซื้อทั้งหมด',
			accessor: 'net',
			width: 120,
			minWidth: 120,
			Cell: ({ value }: any) => <AmountPercentage value={value} />,
		},
		// {
		// 	Header: '',
		// 	accessor: 'action',
		// 	width: 100,
		// 	minWidth: 100,
		// 	disableSortBy: true,
		// 	Cell: () => (
		// 		<div className="w-100 text-center">
		// 			<button className="btn btn-icon btn-custom btn-active-light rounded-circle">
		// 				<img
		// 					src="/media/icons/kumopack/dots-vertical.png"
		// 					alt="Dot Vertical Icon"
		// 					width={20}
		// 					height={20}
		// 				/>
		// 			</button>
		// 		</div>
		// 	),
		// },
	]

	useEffect(() => {
		setShowContentToolbar(true)
		setCustomContentToolbar(
			<DateFilterToolbar filter={filter} onChangeDateFilter={onChangeDateFilter} />
		)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter])

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter.startPeriod, filter.endPeriod])

	useEffect(() => {
		return () => {
			clearState()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		steppers: steppers,
		data: buyerData,
		reportData,
		TAB_CONFIGS,
	}
}

export default ViewModel
